import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { DayPicker } from 'react-day-picker';
import { IconReports } from '@ui';
import 'react-day-picker/dist/style.css';
import { fetchMergedShiftReports, ReportFilterCriteria } from '../../graphql/reportQueries';
import { ReportExportMenu } from './ReportExportMenu';
import type { NormalizedReportRecord, ReportConfiguration, AggregatedDataPoint } from '../../types/reportTypes';
import { buildReportPath } from '../../utils/reportUrlUtils';
import '../../css/ShiftReportsTab.css';
import '../../css/ReportFilterBar.css';

import type { UnitReportScope } from './ReportFilterBar';

interface ShiftReportsTabProps {
    companyId: string;
    unitScope?: UnitReportScope;
}

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50];
const STORAGE_KEY = 'report-filters-shift';

interface ShiftFilters {
    dateStart: string;
    dateEnd: string;
    userName: string;
}

const EMPTY_SHIFT_FILTERS: ShiftFilters = { dateStart: '', dateEnd: '', userName: '' };

const formatDisplayDate = (isoDate: string): string => {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    return `${month}/${day}/${year}`;
};

export const ShiftReportsTab = ({ companyId, unitScope }: ShiftReportsTabProps) => {
    const navigate = useNavigate();
    const tableRef = useRef<HTMLDivElement>(null);

    const [records, setRecords] = useState<NormalizedReportRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

    const defaultStart = useMemo(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    }, []);

    const [filters, setFilters] = useState<ShiftFilters>(() => {
        try {
            const saved = sessionStorage.getItem(STORAGE_KEY);
            if (saved) return JSON.parse(saved) as ShiftFilters;
        } catch { /* ignore */ }
        return { dateStart: defaultStart, dateEnd: '', userName: '' };
    });

    const [showStartPicker, setShowStartPicker] = useState(false);
    const [showEndPicker, setShowEndPicker] = useState(false);
    const startPickerRef = useRef<HTMLDivElement>(null);
    const endPickerRef = useRef<HTMLDivElement>(null);

    // Persist filters to sessionStorage
    useEffect(() => {
        try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters)); } catch { /* quota */ }
    }, [filters]);

    // Close pickers on outside click
    useEffect(() => {
        const handleClick = (e: MouseEvent) => {
            if (startPickerRef.current && !startPickerRef.current.contains(e.target as Node)) setShowStartPicker(false);
            if (endPickerRef.current && !endPickerRef.current.contains(e.target as Node)) setShowEndPicker(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    // Fetch reports when companyId or date filters change
    useEffect(() => {
        if (!companyId) return;
        setIsLoading(true);
        setError(null);

        const criteria: ReportFilterCriteria = {};
        if (filters.dateStart) criteria.dateStart = filters.dateStart;
        if (filters.dateEnd) criteria.dateEnd = filters.dateEnd;

        fetchMergedShiftReports(companyId, criteria)
            .then((items) => setRecords(items))
            .catch((err) => {
                console.error('Failed to fetch shift reports:', err);
                Sentry.captureException(err);
                setError('Failed to load shift reports. Please try again.');
            })
            .finally(() => setIsLoading(false));
    }, [companyId, filters.dateStart, filters.dateEnd]);

    // Client-side filtering for user name and optional unit scope
    const filteredRecords = useMemo(() => {
        let result = records;
        if (unitScope?.unitId) {
            result = result.filter((r) => r.unitId === unitScope.unitId);
        }
        if (!filters.userName) return result;
        const search = filters.userName.toLowerCase();
        return result.filter((r) => r.userName?.toLowerCase().includes(search));
    }, [records, filters.userName, unitScope?.unitId]);

    const totalPages = Math.max(1, Math.ceil(filteredRecords.length / pageSize));
    const paginatedRecords = filteredRecords.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    useEffect(() => { setCurrentPage(1); }, [filters]);

    const update = (patch: Partial<ShiftFilters>) => setFilters(prev => ({ ...prev, ...patch }));

    const handleClear = () => {
        setFilters({ ...EMPTY_SHIFT_FILTERS });
        try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    };

    const handleRowClick = (record: NormalizedReportRecord) => {
        const source = record.source === 'LegacyReport' ? 'LegacyReport' : 'ShiftReport';
        navigate(
            buildReportPath({
                id: record.id,
                source,
                slugLabel:
                    record.unitName ||
                    record.equipmentCategoryName ||
                    (record.shiftType === 'end-of-day' ? 'end-of-day-report' : 'start-of-day-report'),
            }),
        );
    };

    const handlePageSizeChange = (newSize: number) => { setPageSize(newSize); setCurrentPage(1); };

    const formatDate = (dateStr: string) => {
        if (!dateStr) return '—';
        try { return new Date(dateStr).toLocaleDateString(); } catch { return dateStr; }
    };

    const formatDuration = (seconds?: number) => {
        if (seconds == null) return '—';
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return mins === 0 ? `${secs}s` : `${mins}m ${secs}s`;
    };

    const formatShiftType = (shiftType?: string) => shiftType === 'end-of-day' ? 'End of Day' : 'Start of Day';

    const exportData: AggregatedDataPoint[] = filteredRecords.map((r) => ({
        groupKey: r.id, groupLabel: r.userName || r.id,
        value: r.okCount + r.warnCount + r.inopCount, count: 1,
    }));

    const exportConfig: ReportConfiguration = {
        metricKey: 'shift-reports', dateRangeStart: filters.dateStart || '',
        dateRangeEnd: filters.dateEnd || '', groupByField: 'user',
        filters: [], visualizationType: 'table',
    };

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        if (totalPages <= 7) { for (let i = 1; i <= totalPages; i++) pages.push(i); }
        else {
            pages.push(1);
            if (currentPage > 3) pages.push('...');
            for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) pages.push(i);
            if (currentPage < totalPages - 2) pages.push('...');
            pages.push(totalPages);
        }
        return pages;
    };

    const hasActiveFilters = filters.dateStart || filters.dateEnd || filters.userName;

    // Chips
    const activeChips: { key: keyof ShiftFilters; label: string }[] = [];
    if (filters.dateStart) activeChips.push({ key: 'dateStart', label: `From: ${formatDisplayDate(filters.dateStart)}` });
    if (filters.dateEnd) activeChips.push({ key: 'dateEnd', label: `To: ${formatDisplayDate(filters.dateEnd)}` });
    if (filters.userName) activeChips.push({ key: 'userName', label: `User: ${filters.userName}` });

    if (isLoading) return <div className="shift-reports-loading">Loading shift reports...</div>;

    if (error) {
        return (
            <div className="shift-reports-empty">
                <div className="shift-reports-empty-title">{error}</div>
                <button className="shift-pagination-btn" style={{ marginTop: 12 }} onClick={() => setFilters({ ...filters })}>Retry</button>
            </div>
        );
    }

    return (
        <div className="shift-reports-tab" ref={tableRef}>
            {/* Filter bar — same style as ReportFilterBar */}
            <div className="report-filter-bar">
                <div className="report-filter-row">
                    <div className="report-filter-group">
                        <label>Date Range</label>
                        <div className="report-filter-date-range">
                            <div className="report-date-picker-wrapper" ref={startPickerRef}>
                                <div className="report-filter-date-btn-wrapper">
                                    <button
                                        type="button"
                                        className={`report-filter-date-btn ${filters.dateStart ? 'has-value' : ''}`}
                                        onClick={() => { setShowStartPicker(p => !p); setShowEndPicker(false); }}
                                        aria-label="Select start date"
                                    >
                                        {filters.dateStart ? formatDisplayDate(filters.dateStart) : 'Start date'}
                                        <span className="report-date-icon">📅</span>
                                    </button>
                                    {filters.dateStart && (
                                        <button type="button" className="report-date-clear-btn"
                                            onClick={(e) => { e.stopPropagation(); update({ dateStart: '' }); }}
                                            aria-label="Clear start date">×</button>
                                    )}
                                </div>
                                {showStartPicker && (
                                    <div className="report-date-picker-popover">
                                        <DayPicker mode="single"
                                            selected={filters.dateStart ? new Date(filters.dateStart + 'T00:00:00') : undefined}
                                            defaultMonth={filters.dateStart ? new Date(filters.dateStart + 'T00:00:00') : new Date()}
                                            onSelect={(date) => {
                                                if (!date) return;
                                                update({ dateStart: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` });
                                                setShowStartPicker(false);
                                            }}
                                            captionLayout="dropdown" fixedWeeks
                                        />
                                    </div>
                                )}
                            </div>
                            <span className="report-filter-date-sep">to</span>
                            <div className="report-date-picker-wrapper" ref={endPickerRef}>
                                <div className="report-filter-date-btn-wrapper">
                                    <button
                                        type="button"
                                        className={`report-filter-date-btn ${filters.dateEnd ? 'has-value' : ''}`}
                                        onClick={() => { setShowEndPicker(p => !p); setShowStartPicker(false); }}
                                        aria-label="Select end date"
                                    >
                                        {filters.dateEnd ? formatDisplayDate(filters.dateEnd) : 'End date'}
                                        <span className="report-date-icon">📅</span>
                                    </button>
                                    {filters.dateEnd && (
                                        <button type="button" className="report-date-clear-btn"
                                            onClick={(e) => { e.stopPropagation(); update({ dateEnd: '' }); }}
                                            aria-label="Clear end date">×</button>
                                    )}
                                </div>
                                {showEndPicker && (
                                    <div className="report-date-picker-popover">
                                        <DayPicker mode="single"
                                            selected={filters.dateEnd ? new Date(filters.dateEnd + 'T00:00:00') : undefined}
                                            defaultMonth={filters.dateEnd ? new Date(filters.dateEnd + 'T00:00:00') : new Date()}
                                            onSelect={(date) => {
                                                if (!date) return;
                                                update({ dateEnd: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` });
                                                setShowEndPicker(false);
                                            }}
                                            captionLayout="dropdown" fixedWeeks
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="report-filter-group">
                        <label htmlFor="shift-filter-user">User Name</label>
                        <input
                            id="shift-filter-user"
                            type="text"
                            className="report-filter-select"
                            placeholder="Search by name..."
                            value={filters.userName}
                            onChange={(e) => update({ userName: e.target.value })}
                            aria-label="Filter by user name"
                        />
                    </div>

                    {hasActiveFilters && (
                        <div className="report-filter-actions">
                            <button className="report-filter-btn" onClick={handleClear}>Clear All</button>
                        </div>
                    )}
                </div>

                {activeChips.length > 0 && (
                    <div className="report-filter-chips">
                        {activeChips.map(chip => (
                            <span key={chip.key} className="report-filter-chip">
                                {chip.label}
                                <button onClick={() => update({ [chip.key]: '' })} aria-label={`Remove ${chip.label} filter`}>×</button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="shift-reports-header">
                <h3>Shift Reports ({filteredRecords.length})</h3>
                <div className="shift-reports-header-actions">
                    <ReportExportMenu data={exportData} config={exportConfig} reportName="Shift Reports" targetRef={tableRef} />
                </div>
            </div>

            {filteredRecords.length === 0 ? (
                <div className="shift-reports-empty">
                    <div className="shift-reports-empty-icon" aria-hidden="true">
                        <IconReports />
                    </div>
                    <div className="shift-reports-empty-title">No shift reports found</div>
                    <div className="shift-reports-empty-text">Try adjusting your filters or check back later.</div>
                </div>
            ) : (
                <>
                    <div className="shift-reports-table-wrapper">
                        <table className="shift-reports-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Shift Type</th>
                                    <th>User Name</th>
                                    <th>OK / Warn / Inop</th>
                                    <th>Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedRecords.map((record) => (
                                    <tr key={record.id} onClick={() => handleRowClick(record)}>
                                        <td>{formatDate(record.createdAt)}</td>
                                        <td>
                                            <span className={`shift-type-badge ${record.shiftType || 'start-of-day'}`}>
                                                {formatShiftType(record.shiftType)}
                                            </span>
                                        </td>
                                        <td>{record.userName || '—'}</td>
                                        <td>
                                            <div className="shift-status-counts">
                                                <span className="shift-count-badge ok">{record.okCount}</span>
                                                <span className="shift-count-badge warn">{record.warnCount}</span>
                                                <span className="shift-count-badge inop">{record.inopCount}</span>
                                            </div>
                                        </td>
                                        <td>{formatDuration(record.inspectionDurationSeconds)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="shift-reports-pagination">
                        <div className="shift-pagination-info">
                            Showing {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, filteredRecords.length)} of {filteredRecords.length}
                            <select className="shift-page-size-select" value={pageSize}
                                onChange={(e) => handlePageSizeChange(Number(e.target.value))} aria-label="Page size">
                                {PAGE_SIZE_OPTIONS.map((size) => (
                                    <option key={size} value={size}>{size} per page</option>
                                ))}
                            </select>
                        </div>
                        <div className="shift-pagination-controls">
                            <button className="shift-pagination-btn" disabled={currentPage <= 1}
                                onClick={() => setCurrentPage((p) => p - 1)}>‹ Prev</button>
                            {getPageNumbers().map((page, idx) =>
                                typeof page === 'string' ? (
                                    <span key={`ellipsis-${idx}`} style={{ padding: '0 4px', color: '#94a3b8' }}>…</span>
                                ) : (
                                    <button key={page}
                                        className={`shift-pagination-btn ${currentPage === page ? 'active' : ''}`}
                                        onClick={() => setCurrentPage(page)}>{page}</button>
                                ),
                            )}
                            <button className="shift-pagination-btn" disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage((p) => p + 1)}>Next ›</button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};
